import { useEffect, useState } from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import {
  BankAccountForSelectFragment,
  SalaryAdvanceSettingsPageBankAccountDocument,
  SalaryAdvanceSettingsPageDocument,
  SaveSalaryAdvancePaymentSettingsDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { BankAccountSelector } from 'modules/banking/components/BankAccountSelector'

type Props = {
  automaticPaymentEnabled: boolean
  bankAccount: BankAccountForSelectFragment | null
  canEdit: boolean
}

const PaymentSettings = ({ automaticPaymentEnabled, bankAccount, canEdit }: Props) => {
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<BankAccountForSelectFragment | null>(bankAccount)

  // Sync state when prop changes (e.g., after initial load or refetch)
  useEffect(() => {
    setSelectedBankAccount(bankAccount)
  }, [bankAccount])

  const [savePaymentSettings, { loading }] = useMutation(SaveSalaryAdvancePaymentSettingsDocument, {
    extractorFn: (payload) =>
      payload.salaryAdvanceMutations?.savePaymentSettingsSalaryadvanceCompanySetting ?? {
        errors: [],
      },
    refetchQueries: [
      SalaryAdvanceSettingsPageDocument,
      SalaryAdvanceSettingsPageBankAccountDocument,
    ],
  })

  const handleToggle = async (newValue: boolean) => {
    const result = await savePaymentSettings({
      variables: {
        automaticPaymentEnabled: newValue,
        bankingBankAccountId: selectedBankAccount?.id?.toString() ?? null,
      },
    })
    const { success } = handleResult(result, () => {})

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  const handleBankAccountSelect = async (bankAccount: BankAccountForSelectFragment | null) => {
    setSelectedBankAccount(bankAccount)

    const result = await savePaymentSettings({
      variables: {
        automaticPaymentEnabled,
        bankingBankAccountId: bankAccount?.id?.toString() ?? null,
      },
    })
    const { success } = handleResult(result, () => {})

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  return (
    <Section.Form
      illustration="wallet"
      title={i18n.t('salary_advance.settings.payment_settings.title')}
      description={i18n.t('salary_advance.settings.payment_settings.description')}
      isDirty={false}
    >
      <Box flexDirection="column" gap="s40">
        <Box flexDirection="row" alignItems="center" gap="s12">
          <Text
            size="200"
            className={
              automaticPaymentEnabled ? 'text-f1-foreground-secondary' : 'text-f1-foreground'
            }
          >
            {i18n.t('salary_advance.settings.payment_settings.disabled_label')}
          </Text>
          <Switch
            id="automatic-payment-toggle"
            checked={automaticPaymentEnabled}
            disabled={!canEdit || loading}
            onCheckedChange={handleToggle}
          />
          <Text
            size="200"
            className={
              automaticPaymentEnabled ? 'text-f1-foreground' : 'text-f1-foreground-secondary'
            }
          >
            {i18n.t('salary_advance.settings.payment_settings.enabled_label')}
          </Text>
        </Box>

        <BankAccountSelector
          label={i18n.t('salary_advance.settings.payment_settings.bank_account_label')}
          emptyLabel={i18n.t('salary_advance.settings.payment_settings.bank_account_placeholder')}
          value={selectedBankAccount}
          onSelect={handleBankAccountSelect}
          disabled={!canEdit || loading}
          width="s400"
        />
      </Box>
    </Section.Form>
  )
}

export default PaymentSettings
